import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, catchError, map, Observable, of, Subject, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

import {
  ClientsResponse, LoanAgreementModel, OfferModel, PreAuthorisationModel, QuoteModel, QuoteStatusHistory,
} from '@lmsModels';

import { OVERRIDE_CTX, RETRY_ALLOWED } from '../interceptors/http-error.interceptor';
import { DEFAULT_TIMEOUTCTX } from '../interceptors/timeout.interceptor';
import { ErrorhanderService } from '../services/error-handler.service';

@Injectable({ providedIn: 'root' })
export class QuoteStoreService implements OnDestroy {
  private quote_apiURL = `${environment.apiUrl}/loan/api/quote`;
  private client_apiURL = `${environment.apiUrl}/loan/api/client`;

  private preAuthorisationModel: PreAuthorisationModel;
  private _isQuoteFound = false;
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  verifiedMessage: any;
  private destroy$ = new Subject<void>();

  constructor(
    private httpClient: HttpClient,
    private errorService: ErrorhanderService,
  ) {}

  get PreAuthorisationModel() {
    return this.preAuthorisationModel;
  }
  set PreAuthorisationModel(value) {
    this.preAuthorisationModel = value;
  }

  /* QUOTENOTES */

  quoteNotes$ = new BehaviorSubject<number>(0);
  addQuoteNote(note: any) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/addQuoteNote`, note, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData) => {
          this.setQuoteNotes(responseData);
          return responseData;
        }),
      );
  }

  getQuoteNotesCount() {
    const ss = this.quoteNotes$.getValue();
    return ss;
  }

  setQuoteNotes(data: any) {
    if (data) {
      this.quoteNotes$.next(data.length);
    }
  }

  getQuoteNotes(id: number) {
    return this.httpClient.get(this.quote_apiURL + '/getQuoteNotes/' + id).pipe(
      map((quoteNotes: any) => {
        this.setQuoteNotes(quoteNotes);
        return quoteNotes;
      }),
    );
  }

  /* QUOTENOTES END */

  setVerifiedMessage(message: any) {
    this.verifiedMessage = message;
  }

  get getVerifiedMessage() {
    return this.verifiedMessage;
  }

  /** quoteData2Subject */

  private quoteData2Subject = new BehaviorSubject<QuoteModel | any>({});
  readonly streamitems$ = this.quoteData2Subject.asObservable();
  get quoteData2$(): Observable<QuoteModel> {
    return this.quoteData2Subject.asObservable().pipe(
      map((data) => {
        this.setisQuoteFound(!!data);
        return data;
      }),
    );
  }
  get quoteData(): any {
    const value = this.quoteData2Subject.getValue();
    this.setisQuoteFound(!!value);
    return value;
  }

  setQuoteWorkbook(data: any) {
    this.setisQuoteFound(!!data);
    this.quoteData2Subject.next(data);
  }

  get quoteWorkbook(): any {
    return this.quoteWorkbookValue;
  }
  get quoteWorkbookValue() {
    return this.quoteData;
  }

  private quoteFound$ = new BehaviorSubject<boolean>(false);
  readonly isQuoteFound$ = this.quoteFound$.asObservable();
  setisQuoteFound(isfound: boolean) {
    this.quoteFound$.next(isfound);
  }
  get isQuoteFound() {
    return this.quoteFound$.getValue();
  }

  public getClientQuoteDetailsRaw(
    idNumber: string,
    onQuoteloaded: (quote: any) => void,
  ): void {
    this.httpClient
      .post<any>(`${this.quote_apiURL}/idNumber`, idNumber, {
        headers: this.httpOptions.headers,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData: any) => {
          return responseData;
        }),
        tap((quote: any) => {
          onQuoteloaded(quote);
        }),
      );
  }

  public getClientQuoteDetailsAny(
    idNumber: string,
    onQuoteloaded: (quote: any) => void,
  ): void {
    this.httpClient
      .post<any>(`${this.quote_apiURL}/idNumber`, idNumber, {
        headers: this.httpOptions.headers,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData: any) => {
          return responseData;
        }),
        tap((quote: any) => {
          onQuoteloaded(quote);
        }),
      );
  }

  handleQuoteLoaded(quote: any): void {
    this.setisQuoteFound(!!quote);

    this.quoteData2Subject.next(quote);
    this.setQuoteWorkbook(quote);
  }

  LoadedQuoteid = (id: number): Observable<any> => {
    const currentQuote = this.quoteData2Subject.getValue();

    if (currentQuote && currentQuote.id === id) {
      return of(currentQuote).pipe(
        tap((quote) => {
          this.handleQuoteLoaded(quote);
        }),
      );
    }

    return this.getQuoteDetailsById(id).pipe(
      tap((quote) => {
        this.handleQuoteLoaded(quote);
      }),
    );
  };

  getClientQuoteDetails(idNumber: any) {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/idNumber`,
      idNumber,
      {
        headers: this.httpOptions.headers,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      },
    );
  }

  getBranchClientQuoteDetails(data: any) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/branchidNumber`, data, {
        headers: this.httpOptions.headers,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData: any) => {
          this.setisQuoteFound(Boolean(responseData) || false);
          return responseData;
        }),
      );
  }

  getQuoteByIdNumber(data: any) {
    this._isQuoteFound = false;
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/idNumber`, data, {
        headers: this.httpOptions.headers,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((qdata) => {
          this.setQuoteWorkbook(qdata);
          return qdata;
        }),
      );
  }

  /*
  Reference: https://angular.io/tutorial/tour-of-heroes/toh-pt6#get-hero-by-id
  */

  get45DayRule(quoteId: number): Observable<any> {
    return this.httpClient
      .get<any>(`${this.quote_apiURL}/45DayRule/${quoteId}`, this.httpOptions)
      .pipe(
        tap((_) => console.log(`Quotedetails by Qid: ${quoteId}`)),
        catchError(this.handleError(`Quotedetails by Qid = ${quoteId}`)),
      );
  }

  getQuoteDetailsById(qId: number): Observable<any> {
    const url = `${this.quote_apiURL}/quoteById`;
    return this.httpClient.get<any>(`${url}/${qId}`, this.httpOptions).pipe(
      //filter(quote => quote != null),
      tap((_) => console.log(`Quotedetails by Qid: ${qId}`)),
      catchError((error) => {
        this.handleError(`Quotedetails by Qid = ${qId}: ${error}`);
        return of(error);
      }),
    );
  }

  getStatusHistory(quoteId: number): Observable<QuoteStatusHistory[]> {
    return this.httpClient
      .get<QuoteStatusHistory[]>(`${this.quote_apiURL}/status-history/${quoteId}`, this.httpOptions)
      .pipe(catchError(this.handleError(`getStatusHistory quoteId = ${quoteId}`, [])));
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   *
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.errorService.handleString(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  getReadOnlyQuoteDetailsById(data: any) {
    this._isQuoteFound = false;
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/quoteId`,
      data,
      {
        headers: this.httpOptions.headers,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      },
    );
  }

  getNewClientsList(data: any): Observable<any> {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/newClients`,
      data,
      {
        headers: this.httpOptions.headers,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      },
    );
  }

  getComplianceList(data: any): Observable<ClientsResponse[]> {
    return this.httpClient.post<ClientsResponse[]>(
      `${this.client_apiURL}/complianceList`,
      data,
      {
        headers: this.httpOptions.headers,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      },
    );
  }

  getAllComplianceList(data: {
    userName: string;
    idNumber: string;
    branchSerial: string;
    fromDate: string;
    toDate: string;
  }): Observable<any> {
    return this.httpClient.post<any>(
      `${this.client_apiURL}/getAllComplianceList`,
      data,
      {
        headers: this.httpOptions.headers,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      },
    );
  }

  getRequotesList(data: any): Observable<any> {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/requote`,
      data,
      {
        headers: this.httpOptions.headers,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      },
    );
  }

  getBranchQuotesList(data: any): Observable<any> {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/branchQuotes`,
      data,
      {
        headers: this.httpOptions.headers,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      },
    );
  }

  bankingDetailsFromEmid(data: QuoteModel) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/emidBankTransactions`, data, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData: any) => {
          return responseData;
        }),
      );
  }

  saveBankDetails(data: any) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/saveBankDetails`, data, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      });
  }

  OfferOverride(quote: any) {
    this._isQuoteFound = false;
    return this.httpClient.post<any>(
      `${this.client_apiURL}/offerOverride`,
      quote,
      { context: new HttpContext().set(RETRY_ALLOWED, true) }
    );
  }

  aulOverride(quote: any) {
    this._isQuoteFound = false;
    return this.httpClient.post<any>(
      `${this.client_apiURL}/aulOverride`,
      quote,
      { context: new HttpContext().set(RETRY_ALLOWED, true) }
    );
  }

  UpdateCodixOverrideRequest(overrideResult: any) {
    return this.httpClient.post<any>(
      `${this.client_apiURL}/offerOverrideResult`,
      overrideResult,
      {
        headers: this.httpOptions.headers,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      }
    );
  }

  saveOffer(quote: any, bmOverride: string | boolean = 'false') {
    this._isQuoteFound = false;
    const bmOverrideStr =
      typeof bmOverride === 'boolean' ? String(bmOverride) : bmOverride;
    const params = new HttpParams().set('bmOverride', bmOverrideStr);
    return this.httpClient.post<any>(`${this.quote_apiURL}/saveOffer`, quote, {
      params,
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  reCalculateOffer(data: any) {
    return this.httpClient.post<any>(`${this.quote_apiURL}/recalcOffer`, data, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  getAllBanks() {
    return this.httpClient.post<any>(`${this.quote_apiURL}/allBanks`, '', {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
      // .pipe(
      //   map((responseData: any) => {
      //     return responseData;
      //   })
      // );
  }

  getDebiCheckBanks() {
    return this.httpClient.post<any>(`${this.quote_apiURL}/debiCheckBanks`, '', {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
      // .pipe(
      //   map((responseData: any) => {
      //     return responseData;
      //   })
      // );
  }

  saveNextOfKin(data: any) {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/saveNextOfKin`,
      data,
      { context: new HttpContext().set(RETRY_ALLOWED, true) }
    );
  }

  saveAddress(data: any) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/saveAddress`, data, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      });
  }

  findAddressesByQuoteId(id: any) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/addressesByQuoteId`, id, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      });
  }

  saveEmployerDetails(data: any) {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/saveEmployment`,
      data,
      { context: new HttpContext().set(RETRY_ALLOWED, true) }
    );
    // .pipe(
    //   map((res: any) => {
    //     //this.quoteWorkbook = responseData;
    //     return res;
    //   })
    // );
  }

  // findEmploymentByQuoteId(id: any) {
  //   return this.httpClient.post<any>(`${this.quote_apiURL}/addressesByQuoteId`, id)
  //     .pipe(
  //       map((responseData: any) => {
  //         //this.quoteWorkbook = responseData;
  //         return responseData;
  //       })
  //     );
  // }

  findAddressById(id: any): any {
    return this.httpClient.get<any>(`${this.quote_apiURL}/getAddress/${id}`);
  }

  saveJournalComment(data: any) {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/saveJournalComment`,
      data,
      { context: new HttpContext().set(RETRY_ALLOWED, true) }
    );
  }

  printPreAgreement(id: any) {
    const httpOptions = {
      responseType: 'blob' as 'json',
      // 'responseType'  : 'blob' as 'json'        //This also worked
    };

    return this.httpClient.get<any>(
      `${this.quote_apiURL}/printAgreement/${id}`,
      httpOptions,
    );
  }

  printMyBenefits(id: any) {
    const httpOptions = {
      //responseType: 'arraybuffer' as 'json'
      responseType: 'blob' as 'json', //This also worked
    };

    return this.httpClient.get<any>(
      `${this.quote_apiURL}/printMyBenefits/${id}`,
      httpOptions,
    );
  }

  printCreditLifeScript(creditLifeData: any): Observable<Blob> {
    return this.httpClient.post(
      `${this.quote_apiURL}/printCreditLifeScript`,
      creditLifeData,
      {
        responseType: 'blob',
        context: new HttpContext().set(RETRY_ALLOWED, true)
      }
    );
  }

  printCreditAgreement(
    id: number,
    reportType: string,
    language: string = 'en',
  ): Observable<Blob> {
    return this.httpClient.get(
      `${this.quote_apiURL}/printCreditAgreement/${id}/${reportType}?language=${language}`,
      {
        responseType: 'blob',
      },
    );
  }

  printCLMSLoanSummary(id: any, ignoreFirstCheck: boolean = false) {
    const httpOptions = {
      responseType: 'blob' as 'json',
    };

    return this.httpClient.get<any>(
      `${this.quote_apiURL}/printCLMSLoanSummary/${id}/${ignoreFirstCheck}`,
      httpOptions,
    );
  }

  printAulLoanConfirmation(id: any) {
    const httpOptions = {
      responseType: 'blob' as 'json',
    };
    return this.httpClient.get<any>(
      `${this.quote_apiURL}/printAulLoanConfirmation/${id}`,
      httpOptions,
    );
  }

  printMyBenefitsTier(description: any) {
    const httpOptions = {
      responseType: 'arraybuffer' as 'json',
      //'responseType': 'blob' as 'json'        //This also worked
    };

    return this.httpClient.get<any>(
      `${this.quote_apiURL}/printMyBenefitsTier/${description}`,
      httpOptions,
    );
  }

  // printCreditLifeScript(data:any) {
  //   const httpOptions = {
  //     responseType: 'arraybuffer' as 'json'
  //   };

  //   return this.httpClient.
  //     post<any>(`${this.quote_apiURL}/printCreditLifeScript`,data, httpOptions);
  // }

  printCreditLifeTermsConditions() {
    const httpOptions = {
      responseType: 'arraybuffer' as 'json',
    };

    return this.httpClient.get<any>(
      `${this.quote_apiURL}/printCreditLifeTermsConditions`,
      httpOptions,
    );
  }

  printAffordabilityBreakdown(quoteId: any) {
    const httpOptions = {
      responseType: 'arraybuffer' as 'json',
    };

    return this.httpClient.get<any>(
      `${this.quote_apiURL}/printAffordabilityBreakdown/${quoteId}`,
      httpOptions,
    );
  }

  printFMBRejectionLetter(id: any) {
    const httpOptions = {
      responseType: 'arraybuffer' as 'json',
    };

    return this.httpClient.get<any>(
      `${this.quote_apiURL}/printFMBRejectionLetter/${id}`,
      httpOptions,
    );
  }

  printAcknowledgement(data: any) {
    const httpOptions = {
      responseType: 'arraybuffer' as 'json',
    };

    return this.httpClient.post<any>(
      `${this.quote_apiURL}/printAcknowledgment`,
      data,
      {
        responseType: httpOptions.responseType,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      }
    );
  }

  rejectOffer(data: any) {
    return this.httpClient.post<any>(`${this.quote_apiURL}/rejectOffer`, data,{
          context: new HttpContext().set(RETRY_ALLOWED, true)
        }).pipe(map((responseData: any) => {
            //this.quoteWorkbook = responseData;
            return responseData;
          })
        );
  }

  submitClientDetails(quote: QuoteModel) {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/submitClientDetails`, quote,
      { context: new HttpContext().set(OVERRIDE_CTX, true) },
    );
    // .pipe(
    //   map((res: any) => {
    //     return res;
    //   })
    // );
  }

  disburseLoan(quote: OfferModel) {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/disburseLoan`,
      quote,
      {context: new HttpContext().set(RETRY_ALLOWED, true)}
    );
    // .pipe(
    //   map((res: any) => {
    //     return res;

    //   })
    // );
  }

  searchAuditTrail(id: string) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/auditIdNumber`, id, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData: any) => {
          return responseData;
        }),
      );
  }
  searchAuditTrailByQuoteId(id: number): Observable<[]> {
    return this.httpClient.post<any>(`${this.quote_apiURL}/auditQuoteById`, id, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
    // .pipe(
    //   map((responseData: any) => {
    //     return responseData;
    //   })
    // );
  }

  compareOffers(id: any) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/auditOffers`, id, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData: any) => {
          return responseData;
        }),
      );
  }

  emidBankTransactionsTest(data: QuoteModel) {
    this._isQuoteFound = false;
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/emidBankTransactionsTest`, data, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData: any) => {
          this.setQuoteWorkbook(responseData);
          return responseData;
        }),
      );
  }

  rejectCompliance(data: any) {
    this._isQuoteFound = false;
    return this.httpClient
      .post<any>(environment.apiUrl + '/loan/api/client/rejectCompliance', data, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData: any) => {
          //this.quoteWorkbook = responseData;
          return responseData;
        }),
      );
  }

  approveCompliance(data: any) {
    return this.httpClient
      .post<any>(
        environment.apiUrl + '/loan/api/client/approveCompliance',
        data,
        {
          context: new HttpContext().set(RETRY_ALLOWED, true)
        }
      )
      .pipe(
        map((responseData: any) => {
          //this.quoteWorkbook = responseData;
          return responseData;
        }),
      );
  }

  doLoanQuote(quoteId: any) {
    const ctx = new HttpContext();
    ctx.set(OVERRIDE_CTX, true);
    ctx.set(DEFAULT_TIMEOUTCTX, 180000);
    ctx.set(RETRY_ALLOWED, true);
    //this._isQuoteFound = false;
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/doLoanQuote`,
      quoteId,
      { context: ctx }
    );
  }

  doWebfinLoanCreate(data: any) {
    //this._isQuoteFound = false;
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/doLoanCreate`,
      data,
      {
        context: new HttpContext()
          .set(DEFAULT_TIMEOUTCTX, 180000)
          .set(OVERRIDE_CTX, true),
      },
    );
  }

  doLoanConfirmOnly(id: number) {
    //this._isQuoteFound = false;
    return this.httpClient.get<any>(`${this.quote_apiURL}/confirmQuote/${id}`);
    // .pipe(
    //   catchError((returnedError) => {
    //     console.error('SERVICE >> LMS Loan Create: ', returnedError);
    //     //Handle the error here
    //     return throwError(() => returnedError); //Rethrow it back to component
    //   })
    // );

    // .pipe(
    //   map((responseData: any) => {
    //     // this.quoteWorkbook = responseData;
    //     this.setQuoteWorkbook(responseData);
    //     return responseData;
    //   })
    // );
  }

  UpdateMyBenefitJournal(quoteId: number) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/journalLoan`, quoteId, {
        headers: this.httpOptions.headers,
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe();
  }

  completeLoanCreate(quoteId: any) {
    this._isQuoteFound = false;
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/completeLoanCreate`,
      quoteId,
      { context: new HttpContext().set(RETRY_ALLOWED, true) }
    );
    // .pipe(
    //   map((responseData: any) => {
    //     // this.quoteWorkbook = responseData;
    //     this.setQuoteWorkbook(responseData);
    //     return responseData;
    //   })
    // );
  }

  updateSecondMandateAuditTrail(data: {
    idNumber: string;
    quoteId: number;
    previousData: null;
    newData: 'YES' | 'NO' | 'NOT_APPLICABLE';
    userName: string;
    userId: number | string;
  }) {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/updateAuditTrail`,
      data,
      { context: new HttpContext().set(RETRY_ALLOWED, true) }
    );
  }

  // calcOffers(idNumber: any) {
  calcOffers(data: any) {
    return this.httpClient
      .post<any>(`${this.client_apiURL}/calcOffers`, data, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        catchError((error) => {
          this.handleError(`CalculateOffer ERROR: ${error}`);
          return throwError(() => error);
        }),
      );
  }

  recoverOffer(data: any) {
    return this.httpClient
      .post<any>(`${this.client_apiURL}/recoverOffers`, data, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData: any) => {
          return responseData;
        }),
      );
  }

  reviewOffer(quoteId: any) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/reviewOffer`, quoteId, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData: any) => {
          // this.quoteWorkbook = responseData;
          return responseData;
        }),
      );
  }

  saveAllDocuments(data: LoanAgreementModel) {
    return this.httpClient.post<LoanAgreementModel>(`${this.quote_apiURL}/saveAllDocuments`, data,
      {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      }
    );
  }

  saveCreditLifeScript(data: any) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/saveCreditLifeScript`, data,{
        context: new HttpContext().set(RETRY_ALLOWED, true)
      });
  }
  updateAllDocuments(data: any) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/updateAllDocuments`, data,{
        context: new HttpContext().set(RETRY_ALLOWED, true)
      });
  }

  saveFingerId(data: any) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/saveFingerId`, data,{
        context: new HttpContext().set(RETRY_ALLOWED, true)
      });
  }

  saveCallCenterRepFingerId(data: any) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/saveCallCenterRepFingerId`, data,{
        context: new HttpContext().set(RETRY_ALLOWED, true)
      });
  }

  auditLogUserFingerVerification(idNum: any, enrollMsg: any) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/auditLogUserFingerVerification`, {
        idNumber: idNum,
        verifyMessage: enrollMsg,
      }, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      });
  }

  auditLogGPSCapture(quoteId: number, latitude: number, longitude: number) {
    return this.httpClient.post<any>(`${this.quote_apiURL}/auditLogGPSCapture`, {
      quoteId,
      latitude,
      longitude
    },{
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData: any) => {
          return responseData;
        })
      );
  }

  auditLogClientFingerVerification(idNum: any, enrollMsg: any) {
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/auditLogClientFingerVerification`, {
        idNumber: idNum,
        verifyMessage: enrollMsg,
      },{
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData: any) => {
          return responseData;
        }),
      );
  }

  //RESET LOAN
  resetLoanApplication(data: any) {
    this._isQuoteFound = false;
    return this.httpClient
      .post<any>(`${this.quote_apiURL}/resetLoanApplication`, data,{
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((responseData: any) => {
          this.setQuoteWorkbook(responseData);
        })
      );
  }

  saveConfirmations(data: any) {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/saveConfirmations`,
      data,{
        context: new HttpContext().set(RETRY_ALLOWED, true)
      }
    );
    // .pipe(
    //   map((responseData: any) => {
    //     return responseData;
    //   })
    // );
  }

  saveNewLoanConfirmations(data: any) {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/saveNewLoanConfirmations`,
      data,{
        context: new HttpContext().set(RETRY_ALLOWED, true)
      }
    );
    // .pipe(
    //   map((responseData: any) => {
    //     return responseData;
    //   })
    // );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  compuscanCall(quoteId: any) {
    const ctx = new HttpContext();
    ctx.set(OVERRIDE_CTX, true);
    ctx.set(DEFAULT_TIMEOUTCTX, 180000);
    ctx.set(RETRY_ALLOWED, true);

    return this.httpClient.post<any>(
      `${this.quote_apiURL}/compuscanCall`,
      quoteId,
      { context: ctx },
    );
  }

  fetchIncomeFactorParams(income: any) {
    return this.httpClient.post<any>(
      `${this.client_apiURL}/fetchIncomeFactorParams`,
      income,{
        context: new HttpContext().set(RETRY_ALLOWED, true)
      }
    );
  }

  cancelApplication(data: any) {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/cancelApplication`,
      data,{
        context: new HttpContext().set(RETRY_ALLOWED, true)
      }
    );
  }

  uploadApprovalDocument(formData: FormData): Observable<any> {
    return this.httpClient.post<any>(
      `${this.client_apiURL}/uploadApprovalDocument`,
      formData,{
        context: new HttpContext().set(RETRY_ALLOWED, true)
      }
    );
  }

  saveLoanChecklist(checklist: any) {
    return this.httpClient.post<any>(
      `${this.quote_apiURL}/saveLoanChecklist`,
      checklist,{
        context: new HttpContext().set(RETRY_ALLOWED, true)
      }
    );
  }

  sendRegionalManagerEmail(quoteId: number): Observable<any> {
    return this.httpClient.post(
      `${this.client_apiURL}/sendRegionalManagerEmail`,
      { quoteId },
      { context: new HttpContext().set(RETRY_ALLOWED, true) }
    );
  }

  requestAMApproval(quoteId: number, approvalType: 'AM' | 'RM', userName: string): Observable<any> {
    return this.httpClient
      .post(`${this.client_apiURL}/requestLTApproval`, {
        quoteId,
        approvalType,
        initiatedBy: userName
      }, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        tap(() => {
          console.log(`LT approval request submitted: ${approvalType}`);
        }),
        catchError((error) => {
          console.error('Error requesting LT approval:', error);
          return throwError(() => error);
        })
      );
  }

  getAMApprovalQueue(approvalType: 'AM' | 'RM',  status: 'pending', areas?: string): Observable<QuoteModel[]> {
    let params = new HttpParams();
    params = params.set('type', approvalType).set('status', status);

    if (areas && areas.length > 0) {
      params = params.set('areas', areas);
    }

    return this.httpClient.get<QuoteModel[]>(`${this.client_apiURL}/ltApprovalQueue`, { params })
      .pipe(
        catchError((error) => {
          console.error('Error fetching LT approval queue:', error);
          return of([]);
        })
      );
  }

  approveLTLoan(approveRequest: {quoteId: number; approverUsername:string; comments?: string}): Observable<any> {
    return this.httpClient
      .post(`${this.client_apiURL}/approveLTLoan`, approveRequest, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        tap(() => {
          console.log('LT loan approved:', approveRequest.quoteId);
        }),
        catchError((error) => {
          console.error('Error approving LT loan:', error);
          return throwError(() => error);
        })
      );
  }

  rejectLTLoan(quoteId: number, reason: string): Observable<any> {
    return this.httpClient
      .post(`${this.client_apiURL}/rejectLTLoan`, {
        quoteId,
        rejectionDate: new Date(),
        reason,
      }, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        catchError((error) => {
          console.error('Error rejecting LT loan:', error);
          return throwError(() => error);
        })
      );
  }

  sendCBSAuthorizationEmail(quoteId: number): Observable<any> {
    return this.httpClient
      .post(
        `${this.client_apiURL}/sendCBSAuthorizationEmail`,
        {
          quoteId,
          recipient: 'ltloan@finbond.co.za',
        },
        {
          context: new HttpContext().set(OVERRIDE_CTX, true).set(RETRY_ALLOWED, true),
        }
      )
      .pipe(
        tap(() => {
          console.log('CBS authorization email sent:', quoteId);
        }),
        catchError((error) => {
          console.error('Error sending CBS email:', error);
          // Non-blocking error - don't throw
          return of(null);
        })
      );
  }

  logBMApproval(data: { quoteId: number, bmUsername: string, ltStatus: string, initiatedBy: string; timestamp: Date; approvalType: 'BM'; }): Observable<any> {
    return this.httpClient.post(`${this.client_apiURL}/logBMApproval`, data, {
      responseType: 'text', // Assuming the API returns a simple success message
      context: new HttpContext().set(RETRY_ALLOWED, true)
    }).pipe(
       tap(() => {
        console.log('BM approval successfully');
      }),
      catchError((error) => {
        console.error('Error logging BM approval::', error);
        return throwError(() => error);
      })
    );
  }

  uploadDocument(formData: FormData): Observable<any> {
    return this.httpClient.post(`${this.client_apiURL}/uploadDocument`, formData, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    }).pipe(
      tap(() => {
        console.log('Document uploaded successfully');
      }),
      catchError((error) => {
        console.error('Error uploading document:', error);
        return throwError(() => error);
      })
    );
  }
}
